import React, {
    KeyboardEvent,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react'
import {
    Badge,
    Box,
    Button,
    Divider,
    Flex,
    Icon,
    Spinner,
    Text,
} from '@chakra-ui/react'
import { navigate } from 'gatsby'
import {
    FaBell,
    FaArrowUpRightFromSquare,
    FaDoorOpen,
    FaFileLines,
    FaGear,
    FaMagnifyingGlass,
    FaPaw,
    FaRegRectangleList,
    FaStethoscope,
    FaUser,
    FaUserDoctor,
} from 'react-icons/fa6'
import { IconType } from 'react-icons'

import EmptyView from '~components/shared/empty-view'
import SharedModal from '~components/shared/modals/shared-modal'
import SharedSearchInput from '~components/shared/shared-search-input'
import OpenChatButton from '~components/shared/open-chat-button'
import SharedStack from '~components/shared/wrappers/shared-stack'
import { useUniversalSearch } from './use-universal-search'
import { useAskChckvetDock } from '~components/ask-chckvet/ask-chckvet-context'
import {
    askChckvetMascotLabel,
    MascotAvatar,
    useClinicMascot,
} from '~components/ask-chckvet/mascot'
import {
    UniversalSearchClientResult,
    UniversalSearchKind,
    UniversalSearchResult,
    UniversalSearchResultGroup,
} from './types'

interface UniversalSearchModalProps {
    isOpen: boolean
    onClose: () => void
}

export default function UniversalSearchModal({
    isOpen,
    onClose,
}: UniversalSearchModalProps) {
    const inputRef = useRef<HTMLInputElement>(null)
    const [query, setQuery] = useState('')
    const [selectedIndex, setSelectedIndex] = useState(0)
    const { groups, loading, hasQuery } = useUniversalSearch(query)
    const { isEnabled: askEnabled, open: openAskDock } = useAskChckvetDock()

    const flattenedResults = useMemo(
        () => groups.flatMap(group => group.results),
        [groups],
    )

    useEffect(() => {
        if (isOpen) {
            window.setTimeout(() => inputRef.current?.focus(), 0)
        } else {
            setQuery('')
        }
    }, [isOpen])

    useEffect(() => {
        setSelectedIndex(0)
    }, [query, groups.length])

    function handleClose() {
        setQuery('')
        onClose()
    }

    function openAskChckvet() {
        const seedPrompt = query.trim()
        handleClose()
        // A typed question starts its own thread; a bare click reopens
        // whatever the user was last working on.
        openAskDock(seedPrompt ? { seedPrompt, newChat: true } : undefined)
    }

    function handleSelect(result: UniversalSearchResult) {
        navigate(result.route)
        handleClose()
    }

    function handleKeyDown(event: KeyboardEvent<HTMLInputElement>) {
        if (flattenedResults.length === 0) return

        if (event.key === 'ArrowDown') {
            event.preventDefault()
            setSelectedIndex(current => (current + 1) % flattenedResults.length)
        }

        if (event.key === 'ArrowUp') {
            event.preventDefault()
            setSelectedIndex(
                current =>
                    (current - 1 + flattenedResults.length) %
                    flattenedResults.length,
            )
        }

        if (event.key === 'Enter') {
            event.preventDefault()
            handleSelect(flattenedResults[selectedIndex])
        }
    }

    return (
        <SharedModal
            isOpen={isOpen}
            onClose={handleClose}
            title="Search Dashboard"
            size="3xl"
            scrollBehavior="inside"
            contentOverrides={{ rounded: '2xl', overflow: 'hidden' }}
            bodyOverrides={{ p: 0 }}
        >
            <Box
                px={{ base: 4, md: 8 }}
                py={4}
                borderBottomWidth={1}
                borderColor="gray.100"
            >
                <Box w="full" maxW="680px" mx="auto">
                    <SharedSearchInput
                        ref={inputRef}
                        value={query}
                        onChange={event => setQuery(event.target.value)}
                        onKeyDown={handleKeyDown}
                        placeholder="Search settings, pages, clients, patients, clinicians, rooms..."
                        size="lg"
                        groupVariant="filled"
                        bg="gray.50"
                        borderColor="gray.200"
                        rounded="xl"
                    />
                </Box>
            </Box>
            <Box maxH="65vh" overflowY="auto" px={{ base: 4, md: 8 }} py={4}>
                <Box w="full" maxW="680px" mx="auto">
                    {askEnabled && (
                        <AskChckvetEntry
                            query={query.trim()}
                            onClick={openAskChckvet}
                        />
                    )}
                    {!hasQuery ? (
                        <EmptySearchStart askEnabled={askEnabled} />
                    ) : groups.length === 0 && !loading ? (
                        <EmptyView
                            header="No results found"
                            infoText="Try a page, setting, client, patient, service, notification, or audience name."
                        />
                    ) : (
                        <SharedStack spacing={4}>
                            {groups.map(group => (
                                <UniversalSearchGroup
                                    key={group.id}
                                    group={group}
                                    selectedResultId={
                                        flattenedResults[selectedIndex]?.id
                                    }
                                    onSelect={handleSelect}
                                />
                            ))}
                        </SharedStack>
                    )}
                </Box>
            </Box>
        </SharedModal>
    )
}

function AskChckvetEntry({
    query,
    onClick,
}: {
    query: string
    onClick: () => void
}) {
    const { mascot } = useClinicMascot()
    const label = askChckvetMascotLabel(mascot)

    return (
        <Button
            w="full"
            mb={4}
            justifyContent="flex-start"
            leftIcon={<MascotAvatar mascot={mascot} size={28} />}
            variant="outline"
            colorScheme="brand"
            rounded="xl"
            h="auto"
            py={3}
            onClick={onClick}
        >
            <Box textAlign="left" minW={0}>
                <Text fontWeight="semibold" noOfLines={1}>
                    {query ? `${label}: "${query}"` : label}
                </Text>
                <Text fontSize="xs" fontWeight="normal" color="gray.500">
                    {query
                        ? 'Opens a new chat with this question'
                        : 'Questions about your clinic config and dashboard'}
                </Text>
            </Box>
        </Button>
    )
}

function UniversalSearchGroup({
    group,
    selectedResultId,
    onSelect,
}: {
    group: UniversalSearchResultGroup
    selectedResultId: string | undefined
    onSelect: (result: UniversalSearchResult) => void
}) {
    return (
        <Box w="full">
            <Flex align="center" justify="space-between" mb={2}>
                <Text
                    fontSize="xs"
                    fontWeight="bold"
                    color="gray.500"
                    textTransform="uppercase"
                    letterSpacing="wide"
                >
                    {group.label}
                </Text>
                {group.isLoading && <Spinner size="xs" color="gray.400" />}
            </Flex>
            <SharedStack spacing={1} align="stretch">
                {group.results.map(result => (
                    <UniversalSearchResultRow
                        key={result.id}
                        result={result}
                        isSelected={result.id === selectedResultId}
                        onSelect={onSelect}
                    />
                ))}
            </SharedStack>
        </Box>
    )
}

function UniversalSearchResultRow({
    result,
    isSelected,
    onSelect,
}: {
    result: UniversalSearchResult
    isSelected: boolean
    onSelect: (result: UniversalSearchResult) => void
}) {
    const icon = getResultIcon(result.kind)

    return (
        <Box
            role="button"
            tabIndex={0}
            w="full"
            rounded="xl"
            borderWidth={1}
            borderColor={isSelected ? 'brand.200' : 'transparent'}
            bg={isSelected ? 'brand.50' : 'white'}
            _hover={{ bg: 'gray.50', borderColor: 'gray.200' }}
            px={3}
            py={2.5}
            onClick={() => onSelect(result)}
            onKeyDown={event => {
                if (event.key === 'Enter') onSelect(result)
            }}
        >
            <Flex align="center" gap={3}>
                <Flex
                    align="center"
                    justify="center"
                    h={9}
                    w={9}
                    rounded="lg"
                    bg="gray.100"
                    color="gray.600"
                    flexShrink={0}
                >
                    <Icon as={icon} />
                </Flex>
                <Box flex={1} minW={0}>
                    <Flex align="center" gap={2}>
                        <Text
                            fontWeight="semibold"
                            color="gray.900"
                            noOfLines={1}
                        >
                            {result.title}
                        </Text>
                        <Badge
                            colorScheme={getBadgeColor(result.kind)}
                            variant="subtle"
                            flexShrink={0}
                        >
                            {getResultKindLabel(result.kind)}
                        </Badge>
                    </Flex>
                    <Text fontSize="sm" color="gray.500" noOfLines={1}>
                        {result.subtitle}
                    </Text>
                </Box>
                <ResultActions result={result} />
            </Flex>
        </Box>
    )
}

function ResultActions({ result }: { result: UniversalSearchResult }) {
    if (result.kind === 'client') {
        return (
            <Box
                onClick={event => event.stopPropagation()}
                onKeyDown={event => event.stopPropagation()}
            >
                <OpenChatButton
                    client={(result as UniversalSearchClientResult).client}
                >
                    Open chat
                </OpenChatButton>
            </Box>
        )
    }

    return (
        <Button
            size="xs"
            variant="outline"
            colorScheme="gray"
            rightIcon={<Icon as={FaArrowUpRightFromSquare} fontSize="2xs" />}
            rounded="full"
            flexShrink={0}
        >
            Open
        </Button>
    )
}

function EmptySearchStart({ askEnabled }: { askEnabled: boolean }) {
    const { mascot } = useClinicMascot()
    const askLabel = askChckvetMascotLabel(mascot)

    return (
        <Box py={askEnabled ? 4 : 8}>
            <Flex align="center" justify="center" color="gray.400" mb={4}>
                <Icon as={FaMagnifyingGlass} fontSize="3xl" />
            </Flex>
            <Text textAlign="center" fontWeight="semibold" color="gray.700">
                Search everything in Dashboard
            </Text>
            <Text textAlign="center" color="gray.500" fontSize="sm" mt={1}>
                Try "Form writeback", "Christopher", or "New Puppy Exam".
            </Text>
            <Divider my={5} />
            <Text textAlign="center" color="gray.500" fontSize="xs">
                Use arrow keys to move through results and Enter to open one.
                {askEnabled
                    ? ` Or open ${askLabel} above for clinic config help.`
                    : ''}
            </Text>
        </Box>
    )
}

function getResultIcon(kind: UniversalSearchKind): IconType {
    switch (kind) {
        case 'page':
            return FaFileLines
        case 'setting':
            return FaGear
        case 'client':
            return FaUser
        case 'patient':
            return FaPaw
        case 'service':
            return FaStethoscope
        case 'clinician':
            return FaUserDoctor
        case 'room':
            return FaDoorOpen
        case 'notification':
            return FaBell
        case 'audience':
            return FaRegRectangleList
        default:
            return assertExhaustive(kind)
    }
}

function getResultKindLabel(kind: UniversalSearchKind): string {
    switch (kind) {
        case 'page':
            return 'Page'
        case 'setting':
            return 'Setting'
        case 'client':
            return 'Client'
        case 'patient':
            return 'Patient'
        case 'service':
            return 'Service'
        case 'clinician':
            return 'Clinician'
        case 'room':
            return 'Room'
        case 'notification':
            return 'Notification'
        case 'audience':
            return 'Audience'
        default:
            return assertExhaustive(kind)
    }
}

function getBadgeColor(kind: UniversalSearchKind): string {
    switch (kind) {
        case 'page':
            return 'gray'
        case 'setting':
            return 'purple'
        case 'client':
            return 'blue'
        case 'patient':
            return 'green'
        case 'service':
            return 'orange'
        case 'clinician':
            return 'cyan'
        case 'room':
            return 'yellow'
        case 'notification':
            return 'pink'
        case 'audience':
            return 'teal'
        default:
            return assertExhaustive(kind)
    }
}

function assertExhaustive(value: never): never {
    throw new Error(`Unhandled universal search result kind: ${String(value)}`)
}
